// @ts-strict-ignore
import {Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, output} from "@angular/core";
import {Translations} from "@translations";
import {FormControl} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, map} from "rxjs/operators";
import {convertSearchableColumnToSearchTerm, SearchableColumn, SelectableOption} from "@models";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
	selector: "sim-table-column-search",
	templateUrl: "./table-column-search.component.html",
	styleUrls: ["./table-column-search.component.scss"],
	standalone: false,
})
export class TableColumnSearchComponent implements OnChanges {
	T = Translations;

	@Input() isShowing;
	@Input() searchableColumns: SearchableColumn[] = [];

	@Input() searchFilterColumn: any;
	@Input() searchFilterValue: string;

	searchFilterValueChange = output<string>();
	searchFilterColumnChange = output<any>();

	public filterDebounceTime: number = 350;
	public filterInputType: SearchableColumn["type"] = "FreeText";
	public selectableOptions: SelectableOption[] = [];
	public filterInputFormControl = new FormControl();

	@ViewChild("inputElement") inputElement: ElementRef<HTMLInputElement>;

	private _isFocused = false;

	constructor() {
		this.filterInputFormControl.valueChanges
			.pipe(
				untilDestroyed(this),
				filter((searchTerm) => searchTerm != null),
				map((searchTerm) => convertSearchableColumnToSearchTerm(this.filterInputType, searchTerm)),
				debounceTime(this.filterDebounceTime),
				distinctUntilChanged(),
			)
			.subscribe((value) => {
				this.searchFilterValueChange.emit(value);
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.searchFilterColumn) {
			const column = this.searchableColumns.find((c) => c.value === changes.searchFilterColumn.currentValue);
			if (column) {
				this.filterInputType = column.type;

				if (column.type === "Select") {
					this.selectableOptions = column.selectableOptions;
				}
			}
		}

		if (changes.searchFilterValue) {
			switch (this.filterInputType) {
				case "Checkbox": {
					this.filterInputFormControl.setValue(changes.searchFilterValue.currentValue === "true");
					break;
				}
				case "Date": {
					// to prevent the input from being set to the current date while the user is typing
					if (this._isFocused) {
						break;
					}

					this.filterInputFormControl.setValue(changes.searchFilterValue.currentValue);
					break;
				}
				default: {
					this.filterInputFormControl.setValue(changes.searchFilterValue.currentValue);
					break;
				}
			}
		}
	}

	onColumnChange(value: string) {
		const column = this.searchableColumns.find((c) => c.value === value);
		if (column) {
			this.filterInputType = column.type;
			setTimeout(() => {
				// let the change detection run before setting the value
				this.filterInputFormControl.setValue(null);
				this.searchFilterColumnChange.emit(value);
			}, 0);
		}
	}

	focus() {
		if (this.filterInputType === "FreeText" || this.filterInputType === "Date") {
			this.inputElement.nativeElement.focus();
		}
	}

	handleFocus(event?: FocusEvent) {
		this._isFocused = Boolean(event);
	}

	handleColumnSelectClick($event: MouseEvent) {
		// prevent the left hand select from being opened as well
		if (this.filterInputType === "Select") {
			$event.stopPropagation();
		}
	}
}
